import { useRef } from "react";
import { getUserType } from "util/auth";
import { useNavigate } from "react-router-dom";
import { Box, Button, MenuItem, Pagination, Select, Typography } from "@mui/material";
import MenuPriceSetting from "components/menu/MenuPriceSetting";
import TablePriceSetting from "features/proc_fee/components/TablePriceSetting";
import useItemPriceSetting from "features/proc_fee/hooks/useItemPriceSetting";
import SearchItem from "components/SearchItem";

/**
 * Component for setting processing fees.
 */
function ItemPriceSetting() {
  const navigate = useNavigate();
  const {
    tireBrandDetail,
    setTireBrandDetail,
    updateTireBrandDetail,
    categoryOption,
    handlePageChange,
    loading,
    setQueryParams,
    queryParams,
    totalPage,
    currentPage,
    searchValue,
    setSearchValue,
  } = useItemPriceSetting();

  const tableContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Handle page change and scroll table to top
   */
  const handlePageChangeWithScroll = (event: React.ChangeEvent<unknown>, page: number) => {
    handlePageChange(event, page);
    // Scroll table container to top when page changes
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  const onChangeItem = (index: number, key: string, value: any) => {
    setTireBrandDetail((prev) => {
      const newTireBrandDetail = [...prev];
      newTireBrandDetail[index] = {
        ...newTireBrandDetail[index],
        [key]: value,
      };
      return newTireBrandDetail;
    });
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate("/")} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{ display: "flex", gap: 3, flexWrap: "wrap", p: { xs: 2, md: 2.5, lg: 5 } }}
      >
      <Box
        sx={{
          display: { xs: "flex", xl: "block" },
          justifyContent: "space-between",
          flexBasis: { xs: "100%", xl: "290px" },
        }}
      >
        <Typography
          variant="h5"
          margin={0}
          textAlign="left"
          fontSize={{ xs: 24, md: 28, lg: 33 }}
          marginBottom="50px"
          color="var(--gray-600)"
        >
          店舗別 <br /> 各種設定
        </Typography>
        <MenuPriceSetting />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flex: 1,
          width: { xs: "100%", lg: "auto" },
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "auto", ml: { xs: 3, lg: 0 } } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: { xs: 4, xl: 7 },
            }}
          >
            <Box sx={{ width: { xs: "100%", md: "auto" } }}>
              <Typography
                variant="h5"
                marginBottom="12px"
                sx={{ fontSize: { xs: 18, xl: 25 } }}
                color="var(--gray-700)"
                fontWeight="medium"
              >
                タイヤ売価設定
              </Typography>
              <Typography
                variant="h2"
                color="var(--black)"
                fontWeight="regular"
                sx={{ fontSize: { xs: 20, md: 24, xl: 35 } }}
              >
                商品の売価設定を行います
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              justifyContent: { xs: "flex-start", md: "space-between" },
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  width: { xs: "100%", md: "auto" },
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Typography sx={{ fontSize: { xs: "16px", md: "18px", xl: "20px" } }}>
                  中分類
                </Typography>
                <Select
                  sx={{
                    maxWidth: 300,
                    width: { xs: 280, xl: 300 },
                    height: { xs: 32, lg: 48 },
                    fontSize: { xs: "16px", md: "18px", xl: "20px" },
                    color: "var(--gray-700)",
                    "& .MuiSelect-select": {
                      padding: "0 16px",
                      paddingRight: "32px",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  value={queryParams.item_category_m}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span>全て</span>;
                    }
                    const selectedOption = categoryOption.find((opt) => opt.value === selected);
                    const displayText = selectedOption?.label || selected;
                    return (
                      <Box
                        title={displayText}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {displayText}
                      </Box>
                    );
                  }}
                  onChange={(event) => {
                    setQueryParams((prev) => ({ ...prev, item_category_m: event.target.value }));
                  }}
                >
                  <MenuItem value="">全て</MenuItem>
                  {categoryOption.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <SearchItem
                label="メーカー名/ブランド名"
                value={searchValue}
                type="input"
                onChange={(value) => setSearchValue(value as string)}
                containerSx={{ maxWidth: 300 }}
                labelSx={{ height: { xs: 32, lg: 48 }, minHeight: { xs: 32, lg: 48 }, px: 1, minWidth: 'auto' }}
                valueSx={{ "& .MuiOutlinedInput-root": { height: { xs: 32, lg: 48 }, width: '100%' } }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'end', width: { xs: '100%', lg: 'auto' } }}>
              <Box
                sx={{
                  display: "flex",
                  width: { xs: "100%", md: "auto" },
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--black)",
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    fontWeight: "bold",
                    marginRight: "20px",
                  }}
                >
                  {getUserType() === "C0002" ? "黒字：本部/企業設定" : "黒字：本部設定"}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--red-800)",
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    fontWeight: "bold",
                  }}
                >
                  赤字：{getUserType() === "C0006" ? "企業上書設定" : "店舗上書設定"}
                </Typography>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: "var(--black)",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textAlign: 'right',
                }}
              >
                ※1 A表売価掛け率が「0」の場合は、定価を参照
                <br />
                ※2 ホイールセット時の梅花計算方法を「原価から計算」に設定している場合のタイヤ粗利率
              </Typography>
            </Box>
          </Box>
          <Box
            ref={tableContainerRef}
            sx={{
              overflowX: "auto",
              overflowY: "auto",
              width: "100%",
              maxHeight: { xs: "calc(100vh - 250px)", lg: "calc(100vh - 200px)" },
            }}
          >
            <TablePriceSetting
              tireBrandList={tireBrandDetail}
              onChangeItem={onChangeItem}
              onUpdateItem={(index, key) => updateTireBrandDetail(index, key)}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPage}
              page={currentPage}
              onChange={handlePageChangeWithScroll}
              color="primary"
              disabled={loading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
    </>
  );
}

export default ItemPriceSetting;
